import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/theme-dark.css";   // Asegura el estilo oscuro
import "./Clientes.css";             // Para botones pequeños
import Swal from "sweetalert2";

const API = "/api/payment-presets";

export default function PaymentPresets() {
  const navigate = useNavigate();

  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const isEditing = editingId !== null;

  const [form, setForm] = useState({
    nombre: "",
    descripcion: "",
    cuenta: "",
    esPorDefecto: false,
  });

  // ============================
  //   Cargar items
  // ============================
  useEffect(() => {
    const fetchPresets = async () => {
      try {
        setLoading(true);
        const res = await fetch(API);
        if (!res.ok) throw new Error("No se pudieron cargar las formas de pago");
        const data = await res.json();
        setPresets(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPresets();
  }, []);

  // ============================
  //   Helpers Modal
  // ============================
  const openCreate = () => {
    setEditingId(null);
    setForm({
      nombre: "",
      descripcion: "",
      cuenta: "",
      esPorDefecto: false,
    });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      nombre: p.nombre || "",
      descripcion: p.descripcion || "",
      cuenta: p.cuenta || "",
      esPorDefecto: !!p.esPorDefecto,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const onChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  // ============================
  //   Guardar
  // ============================
  const savePreset = async (e) => {
    e.preventDefault();

    if (!form.nombre.trim()) {
      alert("El nombre es obligatorio");
      return;
    }

    try {
      const method = isEditing ? "PUT" : "POST";
      const url = isEditing ? `${API}/${editingId}` : API;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error("No se pudo guardar la forma de pago");

      const json = await res.json();

      if (isEditing) {
        setPresets((p) => p.map((x) => (x.id === editingId ? json : x)));
      } else {
        setPresets((p) => [json, ...p]);
      }

      closeModal();
    } catch (err) {
      setError(err.message);
    }
  };

  // ============================
  //   Eliminar
  // ============================
  const removePreset = async (id) => {
    if (!window.confirm("¿Eliminar esta forma de pago?")) return;

    try {
      const res = await fetch(`${API}/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("No se pudo eliminar");

      setPresets((p) => p.filter((x) => x.id !== id));
    } catch (err) {
      setError(err.message);
    }
  };

  // ============================
  //   Marcar predeterminada
  // ============================
  const makeDefault = async (p) => {
    try {
      const res = await fetch(`${API}/${p.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...p, esPorDefecto: true }),
      });

      if (!res.ok) throw new Error("Error al marcar como predeterminada");

      const updated = await res.json();

      setPresets((prev) =>
        prev.map((x) =>
          x.id === updated.id ? updated : { ...x, esPorDefecto: false }
        )
      );
    } catch (err) {
      setError(err.message);
    }
  };

  // ============================
  //   RENDER
  // ============================
  if (loading) return <div className="page-dark">Cargando…</div>;

  return (
    <div className="page-dark">
      {/* ================================
          HEADER FULL DARK STYLE
      ================================= */}
      <div className="glass-box" style={{ marginBottom: 22 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 20,
          }}
        >
          {/* IZQUIERDA */}
          <div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>
              Formas de pago
            </h1>
            <p style={{ opacity: 0.8, marginTop: 4 }}>
              Gestiona las opciones que podrás usar en presupuestos y facturas.
            </p>
          </div>

          {/* DERECHA */}
          <button
            onClick={() => navigate("/ajustes")}
            className="glass-btn"
            style={{
              padding: "10px 20px",
              fontSize: 15,
              borderRadius: 12,
            }}
          >
            ← Volver
          </button>
        </div>
      </div>

      {/* ===========================================================
          CAJA PRINCIPAL OSCURA
      =========================================================== */}
      <div className="glass-box">
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2 style={{ margin: 0, fontSize: 20 }}>Listado de formas de pago</h2>
          <button
            className="glass-btn"
            style={{ padding: "10px 18px" }}
            onClick={openCreate}
          >
            + Nueva forma de pago
          </button>
        </div>

        {error && (
          <div style={{ marginBottom: 12, color: "#ff8b8b" }}>{error}</div>
        )}

        {/* ============================
            TABLA DARK GLASS REAL
        ============================ */}
        <table className="glass-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Cuenta / IBAN</th>
              <th>Descripción</th>
              <th>Por defecto</th>
              <th style={{ textAlign: "right" }}>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {presets.map((p) => (
              <tr key={p.id}>
                <td style={{ fontWeight: 600 }}>{p.nombre}</td>
                <td>{p.cuenta || "—"}</td>
                <td style={{ maxWidth: 320, opacity: 0.85 }}>
                  {p.descripcion || "—"}
                </td>

                <td>
                  {p.esPorDefecto ? (
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: 999,
                        background: "rgba(16,185,129,0.22)",
                        border: "1px solid rgba(16,185,129,0.35)",
                        color: "#34d399",
                        fontSize: 12,
                      }}
                    >
                      Predeterminada
                    </span>
                  ) : (
                    <button
                      className="btn-small"
                      onClick={() => makeDefault(p)}
                    >
                      Hacer predeterminada
                    </button>
                  )}
                </td>

                {/* Botones derecha */}
                <td style={{ textAlign: "right" }}>
                  <button
                    className="btn-small"
                    onClick={() => openEdit(p)}
                    style={{ marginRight: 8 }}
                  >
                    Editar
                  </button>

                  <button
                    className="btn-small delete"
                    onClick={() => removePreset(p.id)}
                  >
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}

            {presets.length === 0 && (
              <tr>
                <td colSpan="5" style={{ opacity: 0.6, textAlign: "center" }}>
                  No hay formas de pago.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ===========================================
          MODAL FULL DARK GLASS
      =========================================== */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>
                {isEditing ? "Editar forma de pago" : "Nueva forma de pago"}
              </h2>
              <button className="modal-close" onClick={closeModal}>
                ✕
              </button>
            </div>

            <form className="modal-body" onSubmit={savePreset}>
              <label>Nombre *</label>
              <input
                name="nombre"
                className="glass-input"
                value={form.nombre}
                onChange={onChange}
                placeholder="Ej: 50% aceptación / 50% instalación"
              />

              <label>Cuenta / IBAN</label>
              <input
                name="cuenta"
                className="glass-input"
                value={form.cuenta}
                onChange={onChange}
              />

              <label>Descripción</label>
              <textarea
                name="descripcion"
                rows={3}
                className="glass-input"
                value={form.descripcion}
                onChange={onChange}
              />

              <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input
                  type="checkbox"
                  name="esPorDefecto"
                  checked={form.esPorDefecto}
                  onChange={onChange}
                />
                Marcar como predeterminada
              </label>
            </form>

            <div className="modal-footer">
              <button className="glass-btn" onClick={closeModal}>
                Cancelar
              </button>
              <button className="glass-btn" onClick={savePreset}>
                {isEditing ? "Guardar cambios" : "Crear forma de pago"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
